import type { ReactNode } from 'react';
import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';
import { Layout } from './components/Layout';
import { MobileAdminLayout } from './components/MobileAdminLayout';
import { Spinner } from './components/ui/Spinner';
import { LoginPage } from './pages/LoginPage';
import { MobileLoginPage } from './pages/MobileLoginPage';
import { getLoginPath, safeInternalNextPath } from './lib/loginPath';
import { PublicLanding } from './pages/PublicLanding';
import { EntriesPage } from './pages/EntriesPage';
import { LogsPage } from './pages/LogsPage';
import { SitesPage } from './pages/SitesPage';
import { ContentSettingsPage } from './pages/ContentSettingsPage';
import { WechatPage } from './pages/WechatPage';
import { InterfaceInfoPage } from './pages/InterfaceInfoPage';
import { WechatOfficialPushPage } from './pages/WechatOfficialPushPage';
import { HelpPage } from './pages/HelpPage';
import { DashboardPage } from './pages/DashboardPage';
import { UsersPage } from './pages/UsersPage';
import { LoginLogsPage } from './pages/LoginLogsPage';
import { SystemConfigPage } from './pages/SystemConfigPage';
import { AiEnginesPage } from './pages/AiEnginesPage';
import { OssCdnSettingsPage } from './pages/OssCdnSettingsPage';
import { OssCdnAliyunGuidePage } from './pages/OssCdnAliyunGuidePage';
import { MiniprogramsPage } from './pages/MiniprogramsPage';
import { MiniprogramEditPage } from './pages/MiniprogramEditPage';
import { MiniprogramUsersPage } from './pages/MiniprogramUsersPage';
import { MiniprogramAnalyticsPage } from './pages/MiniprogramAnalyticsPage';
import { PromotionAreasPage } from './pages/PromotionAreasPage';
import { PromotionAreaEditPage } from './pages/PromotionAreaEditPage';
import { MicroPromotionForwardLogsPage } from './pages/MicroPromotionForwardLogsPage';
import { MicroPromotionWeworkPushLogsPage } from './pages/MicroPromotionWeworkPushLogsPage';
import { RobotMiniprogramDetailPage } from './pages/RobotMiniprogramDetailPage';
import { RobotManagementPage } from './pages/RobotManagementPage';
import { RobotForwardTasksPage } from './pages/RobotForwardTasksPage';
import { RobotForwardLogsPage } from './pages/RobotForwardLogsPage';
import { SiteMastersPage } from './pages/SiteMastersPage';
import { SiteMasterLoginPage } from './pages/site-master/SiteMasterLoginPage';
import { SiteMasterLayout } from './pages/site-master/SiteMasterLayout';
import { SiteMasterHomePage } from './pages/site-master/SiteMasterHomePage';
import { SiteMasterEntriesPage } from './pages/site-master/SiteMasterEntriesPage';
import { SiteMasterProfilePage } from './pages/site-master/SiteMasterProfilePage';
import { SiteMasterAddPage } from './pages/site-master/SiteMasterAddPage';
import { SiteMasterMpPage } from './pages/site-master/SiteMasterMpPage';
import { SiteMasterAdsPage } from './pages/site-master/SiteMasterAdsPage';
import { SiteMasterForwardPage } from './pages/site-master/SiteMasterForwardPage';
import { SiteMasterAnalyticsPage } from './pages/site-master/SiteMasterAnalyticsPage';
import { MobileDashboardPage } from './pages/mobile/MobileDashboardPage';
import { MobileRegisterPage } from './pages/mobile/MobileRegisterPage';
import { MobileEntriesPage } from './pages/mobile/MobileEntriesPage';
import { MobileUsersPage } from './pages/mobile/MobileUsersPage';
import { MobileAnalyticsPage } from './pages/mobile/MobileAnalyticsPage';
import { useSession } from './hooks/useSession';
import { DocumentTitle } from './components/DocumentTitle';

function MobileSegmentOutlet() {
  return <Outlet />;
}

/** 兼容旧书签：/robots、/robot-miniprograms → 小程序管理 */
function RobotsLegacyRedirect() {
  return <Navigate to="/miniprograms" replace />;
}

/** 兼容旧书签：站点一级入口 → 小程序管理 */
function RobotSitesLegacyRedirect() {
  return <Navigate to="/miniprograms" replace />;
}

function Protected({ children }: { children: ReactNode }) {
  const { ok, loading } = useSession();
  const location = useLocation();
  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-app-mesh">
        <Spinner label="正在验证登录状态" />
      </div>
    );
  }
  if (!ok) {
    const nextRaw = location.pathname + location.search + location.hash;
    const next = safeInternalNextPath(nextRaw);
    const to = next
      ? `${getLoginPath()}?${new URLSearchParams({ next })}`
      : getLoginPath();
    return <Navigate to={to} replace state={{ from: location }} />;
  }
  return <>{children}</>;
}

export default function App() {
  return (
    <>
      <DocumentTitle />
      <Routes>
      <Route path="/" element={<PublicLanding />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="w" element={<Outlet />}>
        <Route path="login" element={<SiteMasterLoginPage />} />
        <Route element={<SiteMasterLayout />}>
          <Route index element={<Navigate to="home" replace />} />
          <Route path="home" element={<SiteMasterHomePage />} />
          <Route path="add" element={<SiteMasterAddPage />} />
          <Route path="entries" element={<SiteMasterEntriesPage />} />
          <Route path="ads" element={<SiteMasterAdsPage />} />
          <Route path="forward" element={<SiteMasterForwardPage />} />
          <Route path="analytics" element={<SiteMasterAnalyticsPage />} />
          <Route path="analytics/:mpId" element={<SiteMasterAnalyticsPage />} />
          <Route path="mp" element={<SiteMasterMpPage />} />
          <Route path="profile" element={<SiteMasterProfilePage />} />
        </Route>
      </Route>
      <Route path="m" element={<MobileSegmentOutlet />}>
        <Route path="login" element={<MobileLoginPage />} />
        <Route element={<Protected><MobileAdminLayout /></Protected>}>
          <Route index element={<Navigate to="dashboard" replace />} />
          <Route path="dashboard" element={<MobileDashboardPage />} />
          <Route path="register" element={<MobileRegisterPage />} />
          <Route path="entries" element={<MobileEntriesPage />} />
          <Route path="analytics" element={<MobileAnalyticsPage />} />
          <Route path="analytics/:mpId" element={<MobileAnalyticsPage />} />
          <Route path="users" element={<MobileUsersPage />} />
        </Route>
      </Route>
      <Route element={<Protected><Outlet /></Protected>}>
        <Route path="oss/guide" element={<OssCdnAliyunGuidePage />} />
        <Route path="oss-cdn/guide" element={<Navigate to="/oss/guide" replace />} />
      </Route>
      <Route element={<Protected><Layout /></Protected>}>
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="entries" element={<EntriesPage />} />
        <Route path="sites" element={<SitesPage />} />
        <Route path="site-masters" element={<SiteMastersPage />} />
        <Route path="users" element={<UsersPage />} />
        <Route path="logs" element={<Navigate to="/log-center" replace />} />
        <Route path="log-center" element={<LogsPage />} />
        <Route path="login-logs" element={<LoginLogsPage />} />
        <Route path="settings" element={<ContentSettingsPage />} />
        <Route path="wechat" element={<WechatPage />} />
        <Route path="interfaces" element={<InterfaceInfoPage />} />
        <Route path="ai-engines" element={<AiEnginesPage />} />
        <Route path="wechat-official-push" element={<WechatOfficialPushPage />} />
        <Route path="oss" element={<OssCdnSettingsPage />} />
        <Route path="oss-cdn" element={<Navigate to="/oss" replace />} />
        <Route path="system-config" element={<SystemConfigPage />} />
        <Route path="miniprograms" element={<MiniprogramsPage />} />
        <Route path="miniprograms/:id/analytics" element={<MiniprogramAnalyticsPage />} />
        <Route path="miniprograms/:id/users" element={<MiniprogramUsersPage />} />
        <Route path="miniprograms/:id" element={<MiniprogramEditPage />} />
        <Route path="promotion-areas" element={<PromotionAreasPage />} />
        <Route path="promotion-areas/:id" element={<PromotionAreaEditPage />} />
        <Route path="micro-promotion-forward-logs" element={<MicroPromotionForwardLogsPage />} />
        <Route path="micro-promotion-wework-push-logs" element={<MicroPromotionWeworkPushLogsPage />} />
        <Route path="robot-miniprograms" element={<Navigate to="/miniprograms" replace />} />
        <Route path="robot-management" element={<RobotManagementPage />} />
        <Route path="robot-miniprograms/:mpId" element={<RobotMiniprogramDetailPage />} />
        <Route path="robot-miniprograms/:mpId/sites/:siteId/tasks" element={<RobotForwardTasksPage />} />
        <Route path="robot-forward-logs" element={<RobotForwardLogsPage />} />
        <Route path="robot-sites" element={<RobotSitesLegacyRedirect />} />
        <Route path="robot-sites/*" element={<RobotSitesLegacyRedirect />} />
        <Route path="robots" element={<RobotsLegacyRedirect />} />
        <Route path="help" element={<HelpPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
}
